import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { IDevice } from "../../../../@types/Devices";
import { Notification } from "../../../../@types/Notification";
import { ISectionProps } from "../../../../@types/Patient";
import { ChevronDown } from "../../../../assets/icons/ChevronDown";
import { ChevronUp } from "../../../../assets/icons/ChevronUp";
import { Dots } from "../../../../assets/icons/Dots";
import { CustomIconButton } from "../../../../components/CustomIconButton";
import { Dropdown, DropdownTextItem } from "../../../../components/Dropdown";
import { ActionContainer } from "../../../../components/TableActionDropdownButton/TableActionDropdownButton.styles";
import { useInfiniteDevices } from "../../../../hooks/queries/devices";
import useIsMobile from "../../../../hooks/useIsMobile";
import { useModal } from "../../../../hooks/useModal";
import { useToast } from "../../../../hooks/useToast";
import { PageTitle } from "../../../../layout/components/PageTitle";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { displayValue } from "../../../../utils/stringFormatter";
import { AddDeviceModal } from "./Components/AddDeviceModal/AddDeviceModal";
import { DeleteDeviceModal } from "./Components/DeleteDeviceModal";
import { DevicesTable } from "./Components/DevicesTable/DevicesTable";
import {
  AddDeviceButton,
  NewDataCOntainer,
  TitleContainer,
} from "./DevicesSection.styles";

// The dropdown emits an item's `value`, falling back to its `text` — which used to be the
// English "Delete". Pinned to an id so the handler no longer depends on the label (EN-5191).
const DELETE_DEVICE_VALUE = "deleteDevice";

export function DevicesSection({
  patient,
  updateColapsedSections,
  collapsedSection,
}: ISectionProps) {
  const { t } = useTranslation();

  const [showNewData, setShowNewData] = useState<boolean>(false);

  const { isMobile } = useIsMobile();
  const {
    devices,
    isFetching,
    isRefetching,
    refetch,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteDevices(patient.id);
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();

  const handleOnClose = (
    notification?: Notification,
    refetchTable?: boolean
  ) => {
    if (notification !== undefined) {
      showToast(notification.message, notification.type);
    }

    if (refetchTable) {
      refetch();
    }

    closeModal();
  };

  const handleDeleteDevice = (content: IDevice) => {
    openModal(<DeleteDeviceModal content={content} onClose={handleOnClose} />, {
      width: "611px",
      height: "363px",
      showCloseButton: true,
    });
  };

  const handleAddDevice = () => {
    openModal(
      <AddDeviceModal patientId={patient.id} onClose={handleOnClose} />,
      {
        width: "auto",
        height: "auto",
        showCloseButton: true,
      }
    );
  };

  const parsedDataToDeviceTable = useMemo(() => {
    if (!devices) return [];

    return devices.map((device: IDevice) => {
      return {
        name: displayValue(device.name),
        added: getLongFormattedDate(device.createdAt, "-"),
        identifier: displayValue(device.identifier),
        firstUsed: getLongFormattedDate(device.firstUsed, "-"),
        lastUsed: getLongFormattedDate(device.lastUsed, "-"),
        batteryLevel:
          device.batteryLevel !== null && device.batteryLevel !== undefined
            ? `${device.batteryLevel}%`
            : "-",
        action: device.identifier ? (
          <Dropdown
            trigger={
              <ActionContainer className="actionContainer">
                <CustomIconButton>
                  <Dots />
                </CustomIconButton>
              </ActionContainer>
            }
            value=""
            onValueChange={(val: string) => {
              if (val === DELETE_DEVICE_VALUE) handleDeleteDevice(device);
            }}
          >
            <DropdownTextItem
              key={DELETE_DEVICE_VALUE}
              value={DELETE_DEVICE_VALUE}
              text={t("patientPage_devices_delete")}
            />
          </Dropdown>
        ) : null,
      };
    });
  }, [devices, t]);

  useEffect(() => {
    if (
      collapsedSection.value === true &&
      !isFetching &&
      !isRefetching &&
      devices &&
      devices.length > 0
    ) {
      const collapsedDate = dayjs(collapsedSection.collapsedDate);

      devices?.forEach((device: IDevice) => {
        const lastAdded = dayjs(device.createdAt);
        const firstUsed = device.firstUsed
          ? dayjs(device.firstUsed)
          : dayjs("2000-01-01");
        const lastUsed = device.lastUsed
          ? dayjs(device.lastUsed)
          : dayjs("2000-01-01");

        if (
          lastAdded > collapsedDate ||
          firstUsed > collapsedDate ||
          lastUsed > collapsedDate
        ) {
          setShowNewData(true);
        }
      });
    }
  }, [devices]);

  const handleCollapseSection = () => {
    if (collapsedSection.value === true) {
      setShowNewData(false);
    }
    updateColapsedSections("devicesSection");
  };

  return (
    <>
      {!isMobile && (
        <TitleContainer>
          <PageTitle>{t("patientPage_devices_title")}</PageTitle>
          {showNewData && (
            <NewDataCOntainer>
              {t("patientPage_sections_newData")}
            </NewDataCOntainer>
          )}
          <CustomIconButton
            style={{ marginLeft: "2px" }}
            onClick={handleCollapseSection}
          >
            {collapsedSection.value ? <ChevronUp /> : <ChevronDown />}
          </CustomIconButton>
        </TitleContainer>
      )}
      <div
        style={{
          height: collapsedSection.value ? "0" : "",
          overflow: collapsedSection.value ? "hidden" : "",
        }}
      >
        <AddDeviceButton
          style={{ display: collapsedSection.value ? "none" : "" }}
          onClick={handleAddDevice}
        >
          {t("patientPage_devices_add")}
        </AddDeviceButton>
        <DevicesTable
          content={parsedDataToDeviceTable}
          isLoading={isFetching && !isRefetching && !isFetchingNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          useInfiniteScroll
        />
      </div>
    </>
  );
}
